import axiosInstance from "./axiosInstance";

/**
 * Visit-analytics beacon: fired once per app load. Best-effort — errors are
 * swallowed by the caller; analytics must never disturb the app.
 */
export async function postVisit(token?: string): Promise<void> {
  await axiosInstance.post(
    "/analytics/visit",
    {
      path: window.location.pathname,
      referrer: document.referrer || undefined,
    },
    token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
  );
}

export type ContactKind = "PHONE" | "EMAIL" | "WHATSAPP" | "WEBSITE";

/**
 * Contact-reveal beacon: fired when a searcher reveals/clicks a listing's
 * contact channel. Best-effort — callers swallow errors; analytics must
 * never disturb the search flow.
 */
export async function postContactReveal(
  inventoryId: string,
  categoryId: string | undefined,
  kind: ContactKind,
  token?: string
): Promise<void> {
  await axiosInstance.post(
    "/analytics/contact-reveal",
    { inventoryId, categoryId, kind },
    token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
  );
}

// ---- analytics panel (ANALYTICS_READ) ----

export interface DayCount { date: string; count: number; }
export interface CategoryCount { categoryId: string; categoryName?: string; count: number; }

export interface ParameterCount { categoryName?: string; parameterName: string; count: number; }

export interface AnalyticsSummary {
  days: number;
  totalVisits: number;
  totalSearches: number;
  zeroResultSearches: number;
  visitsPerDay: DayCount[];
  topCategories: CategoryCount[];
  topParameters: ParameterCount[];
}

export interface SearchLogEntry {
  at?: string;
  userId?: number | null;
  categoryId: string;
  categoryName?: string;
  parameters?: any[];
  coverageChain?: string[];
  geoSearch?: any;
  resultCount: number;
}

export async function getAnalyticsSummary(token: string, days: number): Promise<AnalyticsSummary> {
  const res = await axiosInstance.get<AnalyticsSummary>("/analytics/summary", {
    headers: { Authorization: `Bearer ${token}` },
    params: { days },
  });
  return res.data;
}

export async function getRecentSearches(
  token: string,
  limit: number,
  zeroResultsOnly: boolean
): Promise<SearchLogEntry[]> {
  const res = await axiosInstance.get<SearchLogEntry[]>("/analytics/searches", {
    headers: { Authorization: `Bearer ${token}` },
    params: { limit, zeroResultsOnly },
  });
  return res.data ?? [];
}
